import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, MapPin, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ASSETS, SITE } from "@/lib/site-data";

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 80]);
  const scale = useTransform(scrollY, [0, 600], [1, 1.06]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-28 pb-20 overflow-hidden"
    >
      {/* Parallax background image (logo as scenic backdrop) */}
      <motion.div
        style={{ y, scale }}
        aria-hidden="true"
        className="absolute inset-0 -z-10"
      >
        <img
          src={ASSETS.logo}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Brand-tinted wash so the lush greens read but text stays readable */}
        <div className="absolute inset-0 bg-[#1A2E20]/15" />
        <div className="absolute inset-0 hero-overlay" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-[#D1DED5] text-[#2E5C3E] text-xs font-bold tracking-[0.2em] uppercase mb-7"
            data-testid="hero-eyebrow"
          >
            <MapPin className="w-3.5 h-3.5" />
            Tallahassee · Florida
          </motion.div>

          <motion.h1
            data-testid="hero-headline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.2, 0.8, 0.2, 1] }}
            className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#1A2E20] leading-[0.95]"
          >
            Yards that look <em className="not-italic text-[#2E5C3E]">cared for</em>.
            <br />
            Lawns that look <span className="relative inline-block">
              <span className="relative z-10">royal.</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#EAB308]/70 -z-0 rounded-sm" />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 max-w-xl text-lg md:text-xl text-[#1A2E20]/80 leading-relaxed"
            data-testid="hero-subhead"
          >
            A Tallahassee crew that shows up on time, mows tight, and leaves your
            beds magazine-ready. Weekly maintenance, mulch & flower beds, full
            landscape design — one team, no excuses.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <Button
              data-testid="hero-quote-btn"
              onClick={() => scrollTo("contact")}
              className="group bg-[#EAB308] text-[#422006] hover:bg-[#FACC15] font-bold rounded-full px-7 h-14 text-base shadow-[0_18px_40px_-18px_rgba(234,179,8,0.8)] transition-transform hover:-translate-y-0.5"
            >
              Get a Free Quote
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <a
              data-testid="hero-call-btn"
              href={`tel:${SITE.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 h-14 text-base font-bold bg-[#2E5C3E] text-white hover:bg-[#1B3624] transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call {SITE.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex items-center gap-6 flex-wrap"
            data-testid="hero-trust-row"
          >
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-4 h-4 fill-[#EAB308] text-[#EAB308]" />
              ))}
              <span className="ml-1.5 text-sm font-semibold text-[#1A2E20]">
                5.0 from local neighbors
              </span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-[#D1DED5]" />
            <span className="text-sm text-[#4A6E55] font-medium">
              Licensed & insured · Serving Leon County
            </span>
          </motion.div>
        </div>

        {/* Logo card on right — styled as a yard sign planted in the ground */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
          className="lg:col-span-5 relative"
          data-testid="hero-logo-card"
        >
          <div className="relative max-w-md ml-auto pt-4 pb-24">
            {/* Wooden stake posts driven into the ground */}
            <div
              aria-hidden="true"
              className="absolute left-[22%] top-[14%] bottom-10 w-[14px] -translate-x-1/2 -rotate-[1.5deg] rounded-[3px] shadow-[0_4px_8px_-2px_rgba(0,0,0,0.35)]"
              style={{
                background:
                  "linear-gradient(180deg,#8B5E34 0%,#6B4226 35%,#5a3720 70%,#3d2616 100%)",
                backgroundImage:
                  "linear-gradient(180deg,#8B5E34 0%,#6B4226 35%,#5a3720 70%,#3d2616 100%), repeating-linear-gradient(180deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 1px, transparent 1px, transparent 6px)",
                backgroundBlendMode: "multiply",
              }}
            />
            <div
              aria-hidden="true"
              className="absolute right-[22%] top-[14%] bottom-10 w-[14px] translate-x-1/2 rotate-[1.5deg] rounded-[3px] shadow-[0_4px_8px_-2px_rgba(0,0,0,0.35)]"
              style={{
                background:
                  "linear-gradient(180deg,#8B5E34 0%,#6B4226 35%,#5a3720 70%,#3d2616 100%)",
                backgroundImage:
                  "linear-gradient(180deg,#8B5E34 0%,#6B4226 35%,#5a3720 70%,#3d2616 100%), repeating-linear-gradient(180deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 1px, transparent 1px, transparent 6px)",
                backgroundBlendMode: "multiply",
              }}
            />
            {/* Pointy stake tips peeking below the "ground line" */}
            <div
              aria-hidden="true"
              className="absolute left-[22%] bottom-9 w-[14px] h-3 -translate-x-1/2 -rotate-[1.5deg]"
              style={{
                background: "#3d2616",
                clipPath: "polygon(0 0, 100% 0, 50% 100%)",
              }}
            />
            <div
              aria-hidden="true"
              className="absolute right-[22%] bottom-9 w-[14px] h-3 translate-x-1/2 rotate-[1.5deg]"
              style={{
                background: "#3d2616",
                clipPath: "polygon(0 0, 100% 0, 50% 100%)",
              }}
            />

            {/* The sign itself */}
            <div className="relative aspect-square">
              <div className="absolute -inset-3 bg-[#EAB308] rounded-[2rem] rotate-[-2.5deg] opacity-90" />
              <div className="absolute -inset-3 bg-[#2E5C3E] rounded-[2rem] rotate-[1.5deg] opacity-25" />
              <div className="relative w-full h-full rounded-[2rem] overflow-hidden border-4 border-white shadow-[0_30px_60px_-25px_rgba(26,46,32,0.55)] bg-[#1A2E20]">
                <img
                  src={ASSETS.logo}
                  alt="Imperial Landscaping, TLH logo with lush green outdoor backdrop"
                  className="w-full h-full object-contain"
                />
              </div>
              {/* Stake-bolt detail on the sign (top corners) */}
              <div aria-hidden="true" className="absolute top-3 left-3 w-2.5 h-2.5 rounded-full bg-[#3d2616] shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]" />
              <div aria-hidden="true" className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-[#3d2616] shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]" />
            </div>

            {/* Grass tufts along the ground line */}
            <div
              aria-hidden="true"
              className="absolute left-0 right-0 bottom-7 h-6 pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(ellipse 12px 8px at 8% 100%, #4a7c52 0%, transparent 60%), radial-gradient(ellipse 14px 9px at 22% 100%, #5fa66f 0%, transparent 60%), radial-gradient(ellipse 10px 7px at 38% 100%, #4a7c52 0%, transparent 60%), radial-gradient(ellipse 16px 10px at 55% 100%, #5fa66f 0%, transparent 60%), radial-gradient(ellipse 12px 8px at 72% 100%, #4a7c52 0%, transparent 60%), radial-gradient(ellipse 14px 9px at 88% 100%, #5fa66f 0%, transparent 60%)",
              }}
            />

            {/* Soft ground shadow */}
            <div
              aria-hidden="true"
              className="absolute left-1/2 -translate-x-1/2 bottom-3 w-[78%] h-5 rounded-[50%] bg-[#1A2E20]/35 blur-md"
            />

            <div className="absolute -bottom-2 -left-5 bg-white rounded-2xl px-5 py-3 shadow-[0_18px_40px_-15px_rgba(26,46,32,0.45)] border border-[#D1DED5] animate-float-slower z-10">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#4A6E55] font-bold">
                Family-run · Local
              </div>
              <div className="font-['Outfit'] text-lg font-black text-[#1A2E20] leading-tight">
                Tallahassee, FL
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
